import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AdvisoryDisclosure } from './AdvisoryDisclosure';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type { AdvisorySourceReferenceV01 } from './advisoryProvenance';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import { AdvisoryQuoteLabel, quoteLabelColor, quoteLabelTone } from './AdvisoryQuoteLabel';
import { AdvisorySidebarQuote } from './AdvisorySidebarQuote';

/**
 * The producer cut this source's quote list. Shown whether or not any quote is
 * displayed: without it, a list at the producer's ceiling reads as the whole of
 * the evidence, which is the defect this exists to close.
 *
 * Separate from `quotes.unavailable` on purpose — that one says entries arrived
 * damaged, this one says entries never arrived.
 */
export function QuoteTrimNotice({ source }: { source: AdvisorySourceReferenceV01 }) {
  const { t } = useUntypedTranslation();
  if (!source.quotesTruncated) return null;
  const dropped = source.quotesDropped ?? 0;
  return (
    <Typography role="status" data-quotes-dropped={dropped} sx={{ fontSize: 12 }}>
      {dropped > 0
        ? t('advisory:brief.sources.quotes.truncated_count', { count: dropped })
        : t('advisory:brief.sources.quotes.truncated')}
    </Typography>
  );
}

export function SourceQuotes({
  source,
  quoteReferences,
  initiallyExpanded = false,
  quoteIndex,
}: {
  source: AdvisorySourceReferenceV01;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  initiallyExpanded?: boolean;
  quoteIndex?: number;
}) {
  const { t } = useUntypedTranslation();
  const [expanded, setExpanded] = useState(quoteIndex !== undefined || initiallyExpanded);
  const specific = quoteIndex !== undefined;
  const entries = (source.quotes ?? [])
    .map((quote, index) => ({ quote, index }))
    .filter(entry => !specific || entry.index === quoteIndex);
  const quotes = entries.map(entry => entry.quote);
  return (
    <Box sx={{ marginTop: 1 }}>
      {quotes.length > 0 ? (
        <AdvisoryDisclosure
          title={t(
            specific
              ? 'advisory:brief.sources.selectedQuote'
              : 'advisory:brief.sources.quotes.title'
          )}
          expanded={expanded}
          onToggle={() => setExpanded(value => !value)}
        >
          {quotes.every(quote => quote.status === 'unknown') ? (
            <Typography sx={{ fontSize: 12, marginY: 1 }}>
              {t('advisory:brief.sources.quotes.noChecksHint')}
            </Typography>
          ) : null}
          {entries.map(({ quote, index }) => (
            <Box
              key={index}
              data-source-quote-index={index}
              data-quote-status={quote.status}
              sx={{ marginTop: 2 }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {quoteReferences
                  ?.filter(reference => reference.quoteIndex === index)
                  .map(reference => (
                    <AdvisoryQuoteLabel
                      key={reference.key}
                      label={reference.label}
                      tone={quoteLabelTone(reference.quote.status)}
                    />
                  ))}
                <Typography
                  sx={theme => ({
                    color: quoteLabelColor(theme, quoteLabelTone(quote.status)),
                    fontSize: 12,
                    fontWeight: 600,
                  })}
                >
                  {t(`advisory:brief.sources.quotes.status.${quote.status}`)}
                </Typography>
              </Box>
              <AdvisorySidebarQuote>{quote.text}</AdvisorySidebarQuote>
            </Box>
          ))}
        </AdvisoryDisclosure>
      ) : !source.quotesUnavailable ? (
        <Typography sx={{ fontSize: 12 }}>{t('advisory:brief.sources.quotes.empty')}</Typography>
      ) : null}
      {source.quotesUnavailable ? (
        <Typography role="status" sx={{ fontSize: 12 }}>
          {t('advisory:brief.sources.quotes.unavailable')}
        </Typography>
      ) : null}
      <QuoteTrimNotice source={source} />
    </Box>
  );
}
